/**
 * gltfUpload — Upload 頁的 glTF / ZIP 前處理：把使用者選的檔案變成單一 GLB 的 bytes。
 * 純函式、不碰 React state 與 DOM，方便在 Node 下測試；File 物件由呼叫端組。
 */
import { packFilesToGlb, packEntriesToGlb, looksLikeMultiFileGltf, codedError } from "./gltfPack";
import { unzipToEntries } from "./gltfZip";

/** 這批檔案要不要走 glTF / ZIP 打包流程（否則交給原本的單檔流程）。 */
export const isGltfOrZipUpload = (files) =>
  Array.from(files).some((f) => /\.(gltf|zip)$/i.test(f.name));

/**
 * glTF（.gltf + .bin + 貼圖，多選 / 拖曳資料夾）或 ZIP → { bytes, name }（單一 GLB）。
 * 不是 glTF / ZIP 的情境回 null。
 *
 * maxBytes 用在三處：zip 解壓前依中央目錄宣告的大小擋（見 gltfZip）、打包過程中 BIN 每次累加前擋
 * （見 packGltfToGlb，防止大量重複引用把小輸入放大）、以及打包完成後再對結果檢查一次
 * （也涵蓋 ZIP 內直接取出的 GLB）——ZIP 解開後、或多檔合併加上 JSON / 4-byte 對齊的開銷，
 * 都可能比使用者選的檔案總大小還大，只在選檔當下檢查會漏。
 * @throws {Error & { code?: "OVER_SIZE" | "TOO_MANY_ENTRIES" }} 沒有 code 的錯誤＝缺檔或格式不對
 */
export async function prepareModelFile(files, { maxBytes = Infinity } = {}) {
  const list = Array.from(files);
  let packed;
  if (list.length === 1 && /\.zip$/i.test(list[0].name)) {
    const entries = await unzipToEntries(list[0], { maxTotalBytes: maxBytes });
    const glbs = entries.filter((e) => /\.glb$/i.test(e.path));
    const gltfs = entries.filter((e) => /\.gltf$/i.test(e.path));
    packed =
      gltfs.length === 0 && glbs.length === 1
        ? { bytes: glbs[0].bytes, name: glbs[0].path.split("/").pop() }
        : await packEntriesToGlb(entries, { maxBytes });
  } else if (looksLikeMultiFileGltf(list)) {
    packed = await packFilesToGlb(list, { maxBytes });
  } else {
    return null;
  }
  if (packed.bytes.byteLength > maxBytes) {
    throw codedError(
      "OVER_SIZE",
      `打包後的 GLB ${packed.bytes.byteLength} bytes 超過上限 ${maxBytes} bytes`,
    );
  }
  return { bytes: packed.bytes, name: packed.name };
}
